import { useLocation, Link } from 'react-router-dom';
import {
  LayoutDashboard,
  CheckSquare,
  FileText,
  Briefcase,
  CalendarDays,
  Target,
  GraduationCap,
  Leaf,
  Shield,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import { useIsAdmin } from '@/hooks/useAdmin';
import { GamificationFooter } from '@/components/gamification/GamificationFooter';

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
  { icon: CheckSquare, label: 'Tasks', path: '/tasks' },
  { icon: FileText, label: 'Work Log', path: '/worklog' },
  { icon: Briefcase, label: 'Applications', path: '/applications' },
  { icon: CalendarDays, label: 'Calendar', path: '/calendar' },
  { icon: Target, label: 'Meetings', path: '/meetings' },
  { icon: GraduationCap, label: 'Skills', path: '/skills' },
];

interface AppSidebarProps {
  onNavigate?: () => void;
}

export function AppSidebar({ onNavigate }: AppSidebarProps) {
  const location = useLocation();
  const { data: isAdmin } = useIsAdmin();

  return (
    <aside className="flex h-screen w-[260px] flex-col bg-sidebar relative overflow-hidden flex-shrink-0">
      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-sidebar-primary/5 via-transparent to-sidebar-primary/3 pointer-events-none" />

      {/* Logo — links to landing page */}
      <Link
        to="/"
        className="flex h-16 items-center gap-2.5 px-5 relative group"
        onClick={onNavigate}
      >
        <motion.div
          whileHover={{ rotate: [0, -10, 10, 0] }}
          transition={{ duration: 0.5 }}
          className="flex h-9 w-9 items-center justify-center rounded-xl gradient-primary shadow-lg shadow-primary/30"
        >
          <Leaf className="h-4 w-4 text-white" />
        </motion.div>
        <div>
          <span className="font-display text-sm font-bold text-sidebar-primary-foreground tracking-tight block">
            Velora
          </span>
          <span className="text-[10px] text-sidebar-muted leading-none">
            Your Productivity Hub
          </span>
        </div>
      </Link>

      {/* Divider */}
      <div className="mx-4 h-px bg-sidebar-border/40" />

      {/* Nav */}
      <nav className="flex-1 space-y-0.5 px-3 py-4 relative overflow-y-auto">
        {navItems.map((item) => {
          const active = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={onNavigate}
              className={cn(
                'group relative flex items-center gap-3 rounded-xl px-3 py-3 sm:py-2.5 text-sm font-medium transition-all duration-200',
                active
                  ? 'text-sidebar-primary-foreground'
                  : 'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50'
              )}
            >
              {active && (
                <motion.div
                  layoutId="activeNav"
                  className="absolute inset-0 rounded-xl gradient-primary opacity-90 shadow-lg shadow-primary/20"
                  transition={{ type: 'spring', bounce: 0.15, duration: 0.5 }}
                />
              )}
              <item.icon className={cn(
                'h-4 w-4 shrink-0 relative z-10 transition-all duration-200',
                active ? '' : 'group-hover:scale-110 group-hover:text-sidebar-primary'
              )} />
              <span className="relative z-10">{item.label}</span>
              {active && (
                <motion.div
                  layoutId="activeIndicator"
                  className="absolute right-2 h-1.5 w-1.5 rounded-full bg-white/80 z-10"
                  transition={{ type: 'spring', bounce: 0.15, duration: 0.5 }}
                />
              )}
            </Link>
          );
        })}
      </nav>

      {/* Gamification footer */}
      <div className="relative">
        <GamificationFooter />
      </div>

      {/* Admin link */}
      {isAdmin && (
        <div className="px-3 pb-4 relative">
          <div className="mx-1 mb-2 h-px bg-sidebar-border/40" />
          <Link
            to="/admin"
            onClick={onNavigate}
            className={cn(
              'group relative flex items-center gap-3 rounded-xl px-3 py-3 sm:py-2.5 text-sm font-medium transition-all duration-200',
              location.pathname.startsWith('/admin')
                ? 'text-sidebar-primary-foreground'
                : 'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50'
            )}
          >
            {location.pathname.startsWith('/admin') && (
              <motion.div
                layoutId="activeNav"
                className="absolute inset-0 rounded-xl gradient-primary opacity-90 shadow-lg shadow-primary/20"
                transition={{ type: 'spring', bounce: 0.15, duration: 0.5 }}
              />
            )}
            <Shield className="h-4 w-4 shrink-0 relative z-10" />
            <span className="relative z-10">Admin</span>
          </Link>
        </div>
      )}
    </aside>
  );
}
